import { useEffect, useState } from "react";

import { Label, Typography } from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { FormCard } from "./FormCard";
import styles from "./RequestHeader.module.scss";
import {
  getCommitteeDecisionList,
  getEnrollmentDecision,
} from "../../api/Request/RequestApi";
import { InstitutionCodeBook } from "../../models";
import { Committee } from "../../models/Committee";
import {
  Request,
  RequestStatus,
  RequestType,
  CommitteeDecision,
  OutsideEnrollmentAreaStatus,
  EnrollmentDecision,
} from "../../models/Request";
import { useGetInstitutionListQuery } from "../../queries/codeBookQuery";
import { FormatUtils, MsgUtils } from "../../utils";

export interface RequestHeaderProps {
  request: Request;
}

export const RequestHeader = ({ request }: RequestHeaderProps) => {
  const { t } = useTranslation();
  const [types] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(RequestType, "requestType")
  );
  const { data: institutions } = useGetInstitutionListQuery();
  const [primaryInstitution, setPrimaryInstitution] = useState<
    InstitutionCodeBook | undefined
  >();
  const [committeeDecisions, setCommitteeDecisions] = useState<
    CommitteeDecision[]
  >([]);
  const [enrollmentDecision, setEnrollmentDecision] = useState<
    EnrollmentDecision | undefined
  >();

  useEffect(() => {
    if (!request?.institutionId || !institutions) return;
    setPrimaryInstitution(
      institutions?.find((i) => i.id === request.institutionId)
    );
  }, [request.institutionId, institutions]);
  useEffect(() => {
    (async () => {
      if (request.institutionCommitteeId) {
        var committeeDecisions = await getCommitteeDecisionList(request.id);
        if (committeeDecisions) {
          const institutionCommitteeDecisions = committeeDecisions.filter(
            (v) =>
              v.committeeEmployee?.committeeId ===
              request.institutionCommitteeId
          );
          setCommitteeDecisions(institutionCommitteeDecisions);
        }
      }

      if (request.id !== 0) {
        var enrollmentDecision = await getEnrollmentDecision(request.id);
        setEnrollmentDecision(enrollmentDecision);
      }
    })();
  }, [request.id, request.institutionCommitteeId]);

  if (!request) return null;
  return (
    <FormCard>
      {/* 1st row */}
      <Row>
        <Col md={5}>
          <Typography>
            {request.childDetails?.firstName} {request.childDetails?.lastName},
            OIB: {request.childDetails?.person.oib}
          </Typography>
        </Col>
        <Col md={7}>
          <Typography>
            {t(
              "common.requestModel.requestTabs.institutionCommitteeDecisionInfo"
            )}
            :{" "}
            {committeeDecisions && committeeDecisions.length > 0
              ? t("common.yes")
              : t("common.no")}
          </Typography>
        </Col>
      </Row>
      {/* 2nd row */}
      <Row>
        <Col md={5}>
          <Typography>
            {t("common.schoolCandidateModel.dateOfBirth")}:{" "}
            {request.childDetails?.dateOfBirth
              ? hrDate(request.childDetails?.dateOfBirth)
              : ""}
          </Typography>
        </Col>
        <Col md={7}>
          <Typography>
            {t("request.primaryInstitution")}: {primaryInstitution?.name}
          </Typography>
        </Col>
      </Row>
      {/* 3rd row */}
      <Row>
        <Col md={5}>
          <Typography>
            {t("common.schoolCandidateModel.permanentAddress")}:{" "}
            {FormatUtils.getAddressString(
              request.childDetails?.residence?.permanentAddress,
              request.childDetails?.residence?.address
            )}
          </Typography>
        </Col>
        <Col md={7}>
          <Typography>
            {t("request.hasSecondaryInstitution")}:{" "}
            {request.isOutsideEnrollmentArea
              ? t("common.yes") +
                ", " +
                institutions?.find(
                  (v) =>
                    v.id ===
                    request.outsideEnrollmentAreaInstitutionProgram
                      ?.institutionId
                )?.name
              : t("common.no")}
          </Typography>
        </Col>
      </Row>
      {/* 4th row */}
      <Row>
        <Col md={5}>
          <Typography>
            {t("common.schoolCandidateModel.temporaryAddress")}:{" "}
            {FormatUtils.getAddressString(
              request.childDetails?.residence?.temporaryAddress
            )}
          </Typography>
        </Col>
        {request.isOutsideEnrollmentArea && (
          <>
            <Col md={7}>
              {(!request.outsideEnrollmentAreaStatus ||
                request.outsideEnrollmentAreaStatus ===
                  OutsideEnrollmentAreaStatus.NotProcessed) && (
                <Typography>
                  {t("common.requestModel.status")}:{" "}
                  {t("common.requestModel.notDecided")}
                </Typography>
              )}
              {request.outsideEnrollmentAreaStatus ===
                OutsideEnrollmentAreaStatus.SuggestedEnrollment && (
                <Typography>
                  {t("common.requestModel.status")}:{" "}
                  {t("outsideEnrollmentAreaStatus.SuggestedEnrollment")} (
                  {request.suggestedEnrollmentOutsideEnrollmentArea
                    ? t("common.yes")
                    : t("common.no")}
                  )
                </Typography>
              )}
              {request.outsideEnrollmentAreaStatus ===
                OutsideEnrollmentAreaStatus.Decision && (
                <Typography>
                  {t("common.requestModel.status")}:{" "}
                  {t("outsideEnrollmentAreaStatus.Decision")},{" "}
                  {enrollmentDecision?.solutionId != undefined
                    ? t(
                        "suggestedOutsideEnrollmentArea.approvedByEnrollmentDecision"
                      )
                    : request.approvedEnrollmentOutsideEnrollmentArea
                    ? institutions?.find(
                        (v) =>
                          v.id ===
                          request.outsideEnrollmentAreaInstitutionProgram
                            ?.institutionId
                      )?.name
                    : institutions?.find((v) => v.id === request.institutionId)
                        ?.name}
                </Typography>
              )}
            </Col>
            {/* <Col md={1}>
          <Typography>
            {t("common.requestModel.status")}:
          </Typography>
        </Col>
        <Col md={5}>
          {(!request.outsideEnrollmentAreaStatus || request.outsideEnrollmentAreaStatus === OutsideEnrollmentAreaStatus.NotProcessed) &&
            <Typography>
              {t("common.requestModel.notDecided")}
            </Typography>
          }
          {(request.outsideEnrollmentAreaStatus === OutsideEnrollmentAreaStatus.SuggestedEnrollment) &&
            <Typography>
              {t("outsideEnrollmentAreaStatus.SuggestedEnrollment")} ({request.suggestedEnrollmentOutsideEnrollmentArea ? t("common.yes") : t("common.no")})
            </Typography>
          }
          {(request.outsideEnrollmentAreaStatus === OutsideEnrollmentAreaStatus.Decision) &&
            <Typography>
              {t("outsideEnrollmentAreaStatus.Decision")}, {request.approvedEnrollmentOutsideEnrollmentArea ? institutions?.find(v => v.id === request.outsideEnrollmentAreaInstitutionProgram?.institutionId)?.name : institutions?.find(v => v.id === request.institutionId)?.name }
            </Typography>
          }
        </Col> */}
          </>
        )}
      </Row>
      {/* 5th row */}
      <Row>
        <Col md={2}>
          <Typography>{t("common.requestModel.requestType")}:</Typography>
        </Col>
        <Col md={4} className={styles.labelColumn}>
          <Label
            color={FormatUtils.getRequestTypeColor(request.type)}
            className="text-center"
            outline={false}
            fullWidth={false}
          >
            {types.find((v) => v.id === request.type)?.name}
          </Label>
        </Col>
        {request.enrollmentInstitutionId && (
          <>
            <Col md={6} className={styles.nextToLabelColumn}>
              <Typography>
                {t("common.requestModel.enrollmentInstitution")}:{" "}
                {
                  institutions?.find(
                    (v) => v.id === request.enrollmentInstitutionId
                  )?.name
                }
              </Typography>
            </Col>
          </>
        )}
      </Row>
      {/* 6th row */}
      <Row>
        <Col md={1}>
          <Typography>{t("common.status")}:</Typography>
        </Col>
        <Col md={4} className={styles.labelColumn}>
          <Label
            color={FormatUtils.getRequestStatusColor(request.statusId)}
            className="text-center"
            outline={false}
            fullWidth={false}
          >
            {
              MsgUtils.getEnumToCodeBook(RequestStatus, "requestStatus").find(
                (v) => v.id === request.statusId
              )?.name
            }
          </Label>
        </Col>
      </Row>
    </FormCard>
  );
};
